import {
  FilterItemResource,
  FilterStatusItemType,
  FilterSubStatusItemType,
} from '../../types/FilterTypes';
import {
  BulkExportListResource,
  BulkUpdateMarkInactiveResource,
  BulkUpdateReplacementInfoResource,
  BulkUpdateSendLeadsToCRMResource,
  MailerStatusRequestResource,
  ReassignDealsResource,
} from '../../types/PortfolioBulkActionResource';
import { PortfolioRequestResource } from '../../types/PortfolioRequestResource';
import {
  PortfolioRawResource,
  PortfolioResponseResource,
} from '../../types/PortfolioResponseResource';
import { PortfolioSalesPerson } from '../../types/SalesPerson';
import { BuildPathFromParameters } from '../api/ApiMethods';
import { GetOneFromPath, Post, PostWithReturn, PostWithVoidReturn } from '../api/GenericClient';

const initialPath = '/dashboard';

const mapSubStatusToStatus = (
  statuses: FilterStatusItemType[],
  subStatuses: FilterSubStatusItemType[],
): FilterStatusItemType[] => {
  if (statuses && subStatuses) {
    statuses.forEach((status) => {
      status.subStatuses = subStatuses.filter((x) => x.parentStatus === status.key);
      status.subStatuses.splice(0, 0, {
        key: null,
        count: status.count,
        label: 'All',
      });
    });
  }
  statuses.splice(0, 0, { key: null, count: null, label: 'All Deals' });
  return statuses;
};

async function fetchPortfolioData(
  postData: PortfolioRequestResource,
): Promise<PortfolioResponseResource> {
  const dashboardDataPath = '/portfolioDashboard';
  const apiPath = initialPath.concat(dashboardDataPath);

  const response = await Post<PortfolioRawResource>(apiPath, postData);
  return {
    ...response,
    status: mapSubStatusToStatus(response.status, response.subStatus),
  };
}

async function fetchDealCounts(): Promise<FilterItemResource[]> {
  const dealCountsPath = '/portfolioDealCount';
  const apiPath = initialPath.concat(dealCountsPath);

  return await GetOneFromPath<FilterItemResource[]>(apiPath);
}

async function markInactiveDeals(postData: BulkUpdateMarkInactiveResource): Promise<void> {
  const markInactivePath = '/markinactive';
  const apiPath = initialPath.concat(markInactivePath);

  return await PostWithVoidReturn(apiPath, postData);
}

async function fetchPortfolioAssignee(dealType: string): Promise<PortfolioSalesPerson[]> {
  let apiPath = '/common/salespersoninfo';
  const queryData = { dealType: dealType };
  apiPath = BuildPathFromParameters(apiPath, queryData);

  return await GetOneFromPath<PortfolioSalesPerson[]>(apiPath);
}

async function reassignDeals(postData: ReassignDealsResource): Promise<void> {
  const reassignDealsPath = '/reassign';
  const apiPath = initialPath.concat(reassignDealsPath);

  return await Post(apiPath, postData);
}

async function bulkChangeMailers(postData: MailerStatusRequestResource): Promise<void> {
  const mailerStatusPath = '/bulkchangemailers';
  const apiPath = initialPath.concat(mailerStatusPath);

  return await PostWithVoidReturn(apiPath, postData);
}

async function fetchBulkUpdateReplacementData(
  postData: PortfolioRequestResource,
): Promise<Array<BulkUpdateReplacementInfoResource>> {
  const bulkUpdateReplacement = '/bulkUpdateReplacement';
  const apiPath = initialPath.concat(bulkUpdateReplacement);

  return await Post(apiPath, postData);
}

async function sendLeadsToCRM(postData: BulkUpdateSendLeadsToCRMResource): Promise<void> {
  const sendLeadsToCRMPath = '/bulk/sendToCrm';
  const apiPath = initialPath.concat(sendLeadsToCRMPath);

  return await PostWithVoidReturn(apiPath, postData);
}

async function bulkExportList(postData: BulkExportListResource): Promise<any> {
  const bulkExportPath = '/bulkexport';
  const apiPath = initialPath.concat(bulkExportPath);
  return await PostWithReturn(apiPath, postData);
}

export const PortfolioService = {
  fetchPortfolioData,
  fetchDealCounts,
  markInactiveDeals,
  fetchPortfolioAssignee,
  reassignDeals,
  bulkChangeMailers,
  fetchBulkUpdateReplacementData,
  sendLeadsToCRM,
  bulkExportList,
};
